import { useState } from 'react';
import { ChromeTokens } from './tokens';
import { Icon } from './Icon';

const EASE_SOFT = 'cubic-bezier(0.22, 0.9, 0.3, 1)';

export interface TrustDeviceToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Context-driven subtitle. Defaults to the universal value copy. Pass the
   * "kids" variant ONLY from the user's own in-progress sign-up form (because
   * we know they added kids) — NEVER from a server lookup of the typed email.
   * Branching copy on server account state would leak account info pre-auth.
   */
  subtitle?: string;
}

const DEFAULT_SUBTITLE = 'Stay signed in here — no password again.';

/**
 * TrustDeviceToggle — one progressively-disclosed "Trust this device" control.
 *
 * Replaces the older "stay signed in" / "remember password" / "my own device"
 * toggles. The prominent copy is the value universal to every account (stay
 * signed in, skip the password); the family/kid specifics — only relevant to
 * parents — are tucked into the opt-in "How trusted sign-in works" expander, so
 * the control never feels out of touch to a solo adult.
 *
 * Pure presentational: it owns only the expander's open/closed state. The
 * checked value is controlled by the parent and, on submit, drives whether the
 * caller persists the server-side trusted-device record. Default state is
 * checked (on) — the caller initializes `checked` to true.
 */
export function TrustDeviceToggle({
  checked,
  onChange,
  subtitle,
}: TrustDeviceToggleProps) {
  const [open, setOpen] = useState(false);
  const sub = subtitle ?? DEFAULT_SUBTITLE;
  const borderColor = checked
    ? ChromeTokens.color.grass
    : ChromeTokens.color.baseMuted;

  return (
    <div
      style={{
        borderRadius: ChromeTokens.radius.md,
        overflow: 'hidden',
        border: `1.5px solid ${borderColor}`,
        background: checked
          ? `color-mix(in srgb, ${ChromeTokens.color.grass} 14%, ${ChromeTokens.color.base})`
          : ChromeTokens.color.base,
        transition: `border-color 160ms ${EASE_SOFT}, background 160ms ${EASE_SOFT}`,
        fontFamily: ChromeTokens.font.ui,
      }}
    >
      <label
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          width: '100%',
          padding: '11px 13px',
          cursor: 'pointer',
          boxSizing: 'border-box',
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
        />
        <Icon
          name="shield"
          size={16}
          stroke={ChromeTokens.color.grass}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 700,
              color: ChromeTokens.color.ink,
            }}
          >
            Trust this device
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 12.5,
              color: ChromeTokens.color.inkMuted,
              marginTop: 3,
              lineHeight: 1.45,
            }}
          >
            {sub}
          </span>
        </span>
      </label>

      {/* Expander lives OUTSIDE the <label> so toggling it never flips the
          checkbox. Separated by a top border that tints with the checked state. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          width: '100%',
          minHeight: 44,
          padding: '8px 13px',
          background: 'none',
          border: 'none',
          borderTop: `1px solid ${
            checked
              ? `color-mix(in srgb, ${ChromeTokens.color.grass} 30%, transparent)`
              : ChromeTokens.color.baseMuted
          }`,
          cursor: 'pointer',
          fontFamily: ChromeTokens.font.ui,
          fontSize: 12.5,
          fontWeight: 700,
          color: ChromeTokens.color.inkMuted,
        }}
      >
        <Icon
          name="chevron-down"
          size={14}
          stroke={ChromeTokens.color.inkMuted}
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: `transform 180ms ${EASE_SOFT}`,
          }}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            whiteSpace: 'nowrap',
          }}
        >
          How trusted sign-in works
        </span>
      </button>

      {open && (
        <p
          style={{
            margin: 0,
            padding: '0 13px 12px',
            fontSize: 12.5,
            lineHeight: 1.5,
            color: ChromeTokens.color.inkMuted,
          }}
        >
          Only trust a device your household owns. On a trusted device, anyone
          on the account can tap their picture to sign in — kids go straight in;
          grown-ups re-enter a password so parental controls stay protected.
        </p>
      )}
    </div>
  );
}
